import type { HelpArticle, HelpArticleType } from '../../data/mockData';

export interface HelpIndexEntry {
  id: string;
  slug: string;
  title: string;
  type: HelpArticleType;
  category: string;
  tags: string[];
  preview: string;
  haystack: string;
}

function stripMarkdown(body: string): string {
  return body
    .replace(/```[\s\S]*?```/g, ' ')
    .replace(/`[^`]*`/g, ' ')
    .replace(/!\[[^\]]*\]\([^)]*\)/g, ' ')
    .replace(/\[([^\]]+)\]\([^)]*\)/g, '$1')
    .replace(/[#>*_~-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Cheap AND-substring match against a prebuilt haystack.
 *
 * Used instead of cmdk's built-in fuzzy filter: cmdk scores every item's
 * full `value` on each keystroke, and our haystacks contain entire article
 * bodies. That fuzzy scorer is ~O(n²·m) per item, so scoring tens of
 * thousands of characters across every article froze the UI on each
 * keystroke. A plain lowercased `includes` per token is linear and fast.
 *
 * `haystack` is expected to already be lowercased (see buildHelpIndex).
 */
export function matchesHelpQuery(haystack: string, query: string): boolean {
  const tokens = query.toLowerCase().split(/\s+/).filter(Boolean);
  if (tokens.length === 0) return true;
  return tokens.every(token => haystack.includes(token));
}

export function buildHelpIndex(articles: HelpArticle[]): HelpIndexEntry[] {
  return articles.map(a => {
    const cleanBody = stripMarkdown(a.body || '');
    const preview = cleanBody.slice(0, 160);
    const haystack = [
      a.title,
      a.category,
      ...(a.tags || []),
      cleanBody,
    ].join(' ').toLowerCase();
    return {
      id: a.id,
      slug: a.slug,
      title: a.title,
      type: a.type,
      category: a.category,
      tags: a.tags || [],
      preview,
      haystack,
    };
  });
}
